/**
 * B76B lane B — every reader-facing string on the coach-athlete boundary
 * surfaces, in ONE plain file.
 *
 * Same posture as `onboardingAgeCopy.ts` (REL-29) and `onboardingSaveCopy.ts`
 * (B26): no React, no router, no CSS in the graph, so the strings are readable
 * and pinnable on their own and a founder ratifying copy finds every word in
 * one place.
 *
 * ✅ FOUNDER-RATIFIED — the coach-athlete paywall handoff (design_handoff_
 * coach_athlete_paywall/README.md), screens 1a, 1b, 1c and 4a-4c. Copy here is
 * VERBATIM from that document. It is pinned character-for-character by
 * `coachAthletePaywallCopy.register.test.ts`; if the founder rules again, the
 * pin moves WITH the string.
 *
 * ⚖️ ONE RATIFICATION AMENDMENT that overrides the handoff document:
 *   1a CHANGE 1 ("Ask a coach" → "Ask Rosa") is STRUCK. Rosa is not
 *   conversational, so for a coach the elevated ask-a-coach rail action
 *   DISAPPEARS ENTIRELY with no replacement; Rosa stays on her existing
 *   surfaces. There is therefore no "Ask Rosa" string in this file, and its
 *   absence is itself pinned by test.
 *   1a CHANGE 2 (the quiet "Train yourself" footer row) IS ratified and is
 *   built — `RAIL_FOOTER` below.
 *
 * House register (handoff §Copy rules), enforced by the register test:
 *  - Plain sentences. No hype, no exclamation marks, never the word "safe".
 *  - NO em dashes or en dashes in any authored copy (founder rule).
 *  - Pricing only ever "$12.99 a month" / "$129.99 a year". No new SKUs, no
 *    savings claims.
 */

/** The only two price strings that may appear on a Tayro surface. */
export const ATHLETE_PRICE_MONTHLY = '$12.99 a month';
export const ATHLETE_PRICE_ANNUAL = '$129.99 a year';

/**
 * 1a — the persistent quiet rail footer row (CHANGE 2, ratified). The ONLY
 * persistent upsell in the product.
 */
export const RAIL_FOOTER = {
  label: 'Train yourself',
  sub: `Athlete membership, ${ATHLETE_PRICE_MONTHLY}`,
} as const;

/** 1b — the Rosa row that heads the coach's /chat surface. */
export const ROSA_ROW = {
  name: 'Rosa',
  initial: 'R',
  sub: 'Your team analyst. Weekly reads, plan reviews.',
} as const;

/** 1b — the /chat boundary card, below the Rosa row. */
export const CHAT_BOUNDARY = {
  headline: 'The five athlete coaches train athletes.',
  body: 'Airo, Gair, Vaira, Onai and Kairu coach people who train with Tayro. Your coaching side is complete and free. Training yourself is the athlete side.',
  cta: 'Add athlete training',
  subLine: `${ATHLETE_PRICE_MONTHLY} or ${ATHLETE_PRICE_ANNUAL}. Cancel any time.`,
} as const;

/**
 * 1c — the full-screen athlete-surface boundary. Served wherever a coach lands
 * on an athlete surface (/today, /plan, athlete deep links). Same card, same
 * two lines, everywhere.
 */
export const ATHLETE_SURFACE_BOUNDARY = {
  kicker: 'Athlete side',
  headline: 'Personal training plans are the athlete side of Tayro.',
  body: 'Everything you coach with, Rosa included, is free and stays free. A personal plan, the five athlete coaches and run tracking come with the athlete membership.',
  cta: 'Add athlete training',
  subLine: `${ATHLETE_PRICE_MONTHLY} or ${ATHLETE_PRICE_ANNUAL}`,
  backLink: 'Back to your team',
} as const;

/** 4a-4c — the Settings "This account" card. */
export const THIS_ACCOUNT = {
  title: 'This account',
  subtitle: 'What Tayro is set up for here',

  /** The role word, 22px/700. Reads the ONE role fact. */
  roleWord: {
    athlete: 'Athlete',
    coach: 'Coach',
    athlete_coach: 'Athlete and coach',
  },

  /** Status pills. On = --ok-soft/--ok; Off = --ink-100/--ink-500. */
  pillOn: 'On',
  pillOff: 'Off',

  /** The two side rows. */
  athleteSideName: 'Athlete side',
  coachingSideName: 'Coaching side',

  /** 4a — athlete. */
  athlete: {
    athleteSideSub: 'Your training, plans and coaches',
    coachingSideSub: 'Free. Turns on when you create a team.',
    coachingSideLink: 'Create a team ›',
    /** 13-17 (handoff 3d): the sub is replaced and the link is ABSENT. */
    coachingSideSubMinor: 'For adults',
  },

  /** 4b — coach. */
  coach: {
    coachingSideSub: 'Your team, meets and Rosa. Free.',
    athleteSideSub: 'Comes with the athlete membership',
    athleteSideLink: 'Add athlete training ›',
  },

  /** 4c — athlete_coach. Both sides On. */
  athleteCoach: {
    /** `renews {date}` — the date is formatted by the surface. */
    athleteSideSub: (renewsOn: string) => `Athlete membership, renews ${renewsOn}`,
    /** `{team name}. Free.` */
    coachingSideSub: (teamName: string) => `${teamName}. Free.`,
    link: 'Manage subscription ›',
  },
} as const;

/**
 * 2.0 wave 2, W2-SET-SCREENS (spec 7.6, SS-10) — the Settings "This account"
 * card in plain words, behind `settings_2_0_v1`. Appendix C rows 33 to 40,
 * word for word. The legacy `THIS_ACCOUNT` above is untouched and is what the
 * flag-off card prints. Pinned by `settings.v2.register.test.tsx`.
 *
 * No On or Off pills, no "side", no role word. Dual: Training then Coaching.
 * Coach only: Coaching then Training.
 */
export const THIS_ACCOUNT_V2 = {
  /** 33 */
  title: 'This account',
  /** 34 */
  training: 'Training',
  /** 35 */
  coaching: 'Coaching',
  /** 36: `renewsOn` is the long-month date; the empty "renews " of B391 never prints. */
  trainingLine: (renewsOn: string) =>
    renewsOn ? `Athlete membership, renews ${renewsOn}` : 'Athlete membership',
  /** 37: `{team name}, free`. */
  coachingLine: (teamName: string) => `${teamName}, free`,
  /** 38 */
  coachOnlyCoachingLine: 'Your team, meets and Rosa. Free.',
  /** 39 */
  coachOnlyTrainingLine: 'Comes with the athlete membership',
  /** 40 */
  addAthleteTraining: 'Add athlete training',
} as const;
